import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useTransform, useSpring } from "framer-motion";
import { ChevronDown, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n";
import { IMG } from "@/config/site";
import { GoldParticles } from "@/components/common";

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className="block"
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const imgX = useTransform(sx, [-0.5, 0.5], [-16, 16]);
  const imgY = useTransform(sy, [-0.5, 0.5], [-12, 12]);
  const glowX = useTransform(sx, [-0.5, 0.5], [30, -30]);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      className="section-pad relative flex min-h-screen items-center overflow-hidden pt-24"
      data-testid="hero-section"
    >
      <GoldParticles count={22} />
      <motion.div
        aria-hidden="true"
        style={{ x: glowX }}
        className="pointer-events-none absolute right-[-8%] top-[8%] h-[34rem] w-[34rem] rounded-full bg-gold/[0.13] blur-[130px]"
      />
      <div aria-hidden="true" className="pointer-events-none absolute left-[-12%] bottom-[-10%] h-[30rem] w-[30rem] rounded-full bg-brgreen/[0.14] blur-[120px]" />

      <div className="relative z-10 grid w-full items-center gap-14 lg:grid-cols-[1.15fr_0.85fr]">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="eyebrow"
            data-testid="hero-eyebrow"
          >
            {t("hero.eyebrow")}
          </motion.p>

          <h1 className="mt-6 font-display uppercase leading-[0.88] tracking-tight" data-testid="hero-title">
            <MaskedLine delay={0.25}>
              <span className="text-[17vw] text-paper sm:text-8xl lg:text-[7.5rem]">{t("hero.title1")}</span>
            </MaskedLine>
            <MaskedLine delay={0.38}>
              <span className="gold-text text-[17vw] sm:text-8xl lg:text-[7.5rem]">{t("hero.title2")}</span>
            </MaskedLine>
            <MaskedLine delay={0.52}>
              <span className="mt-3 block font-heading text-lg font-bold tracking-[0.3em] text-neon sm:text-2xl">
                {t("hero.sub")}
              </span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-7 max-w-xl text-sm leading-relaxed text-mist sm:text-base"
            data-testid="hero-text"
          >
            {t("hero.text")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link to="/tokenomics" className="btn-neon" data-testid="hero-cta-explore">
              {t("hero.cta1")} <ArrowRight size={15} />
            </Link>
            <Link to="/nfts" className="btn-gold" data-testid="hero-cta-nfts">{t("hero.cta2")}</Link>
            <Link to="/whitepaper" className="btn-ghost" data-testid="hero-cta-whitepaper">{t("hero.cta3")}</Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto hidden w-full max-w-md sm:block"
          data-testid="hero-art"
        >
          <motion.div style={{ x: imgX, y: imgY }} className="animate-float-slow relative">
            <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-gold/25 via-transparent to-brgreen/25 blur-2xl" aria-hidden="true" />
            <div className="relative overflow-hidden rounded-[2rem] border border-gold/30 shadow-[0_40px_120px_-30px_rgba(212,175,55,0.4)]">
              <img src={IMG.hero} alt="MAGABRAZIL golden coin artwork" className="aspect-[4/5] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-ink/20" />
              <div className="absolute bottom-5 left-5 right-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neon">{t("hero.eyebrow")}</p>
                <p className="mt-1 font-display text-2xl uppercase text-paper">$MBGA</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-7 left-1/2 z-10 -translate-x-1/2"
        aria-hidden="true"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="flex flex-col items-center gap-1 text-mist"
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.35em]">{t("hero.scroll")}</span>
          <ChevronDown size={16} className="text-gold" />
        </motion.div>
      </motion.div>
    </section>
  );
}
